import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap, take } from 'rxjs';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import type { Api } from 'bimplus-websdk';
import { ROUTE } from '../../feature-seatmap';
import { UrlFlagsService } from '../../feature-seatmap/services/url-flags/url-flags.service';
import { AuthStoreService } from './auth-store.service';

@Injectable({ providedIn: 'root' })
export class BimplusUserIdResolverService {
  private readonly authStoreService = inject(AuthStoreService);
  private readonly bimplusApi = inject<Api | null>(BIMPLUS_API, { optional: true });
  private readonly router = inject(Router);
  private readonly urlFlagsService = inject(UrlFlagsService);

  resolveCurrentUserId$(): Observable<string> {
    const userId = this.authStoreService.bimplusUserId();
    if (userId) {
      return of(userId);
    }

    return this.authStoreService.accessToken$.pipe(
      take(1),
      switchMap(accessToken => {
        if (!accessToken || !this.bimplusApi) {
          return of('');
        }

        return from(this.bimplusApi.user.get(accessToken)).pipe(
          map(user => user?.id ?? ''),
          catchError((error: unknown) => {
            // A rejected token means a stale session; without a reset the terms guard loops.
            if (
              (error as { status?: number } | null)?.status === 401 &&
              !this.urlFlagsService.embedded()
            ) {
              this.authStoreService.resetSession();
              void this.router.navigate(['/', ROUTE.login]);
            }
            return of('');
          })
        );
      }),
      map(resolvedUserId => {
        if (resolvedUserId) {
          this.authStoreService.updateBimplusUserId(resolvedUserId);
        }

        return resolvedUserId;
      })
    );
  }
}
